/**
 * The words for a projection period mode, in one place.
 *
 * `projectionPeriodMode` is a real modelling choice, not a display filter: the engine PROJECTS the
 * year it REPORTS, so a calendar-year run opens on 1 January, applies growth there, and strikes its
 * tax, pension and net worth on 31 December (see `engineTypes.ts`). Every surface that describes a
 * projection therefore has to describe the mode the reader actually chose — the projections modal,
 * the Statement of Advice, and the digital SOA that outlives them both.
 *
 * Those three had grown three separate sets of hardcoded "1 July to 30 June" strings, which is how a
 * calendar-year run ends up documented as a financial-year one. This module is the single vocabulary
 * they share, so a document can never name a period the numbers underneath it were not cut on.
 *
 * `'strategy_year'` — the engine's third mode, rolling 12-month bands from the anchor — has no UI and
 * dates nothing, so it is described here only enough to keep callers total.
 */

/** The two modes the UI offers. The engine's `'strategy_year'` is not selectable. */
export type DatedProjectionPeriodMode = 'financial_year' | 'calendar_year';

export interface ProjectionPeriodDescription {
    mode: DatedProjectionPeriodMode;
    /** "financial year" — lower case, for mid-sentence use. */
    noun: string;
    /** "financial years" */
    nounPlural: string;
    /** "1 July" — the day a full period opens on, and growth is applied. */
    opensOn: string;
    /** "30 June" — the day the period settles: totals struck, tax and net worth reported. */
    closesOn: string;
    /** "1 July to 30 June" */
    span: string;
    /** "FY26/27"-style labels, vs calendar mode's bare year — what a row heading looks like. */
    rowLabelExample: string;
    /**
     * True when the reporting period differs from the Australian tax year, so anything the ATO or
     * Centrelink measures on a financial year is being re-cut onto a period they do not use. Only the
     * calendar-year mode is out of step; it is the one caveat a calendar-year document must carry.
     */
    differsFromTaxYear: boolean;
}

const FINANCIAL_YEAR: ProjectionPeriodDescription = {
    mode: 'financial_year',
    noun: 'financial year',
    nounPlural: 'financial years',
    opensOn: '1 July',
    closesOn: '30 June',
    span: '1 July to 30 June',
    rowLabelExample: 'FY26/27',
    differsFromTaxYear: false,
};

const CALENDAR_YEAR: ProjectionPeriodDescription = {
    mode: 'calendar_year',
    noun: 'calendar year',
    nounPlural: 'calendar years',
    opensOn: '1 January',
    closesOn: '31 December',
    span: '1 January to 31 December',
    rowLabelExample: '2027',
    differsFromTaxYear: true,
};

/**
 * The vocabulary for a mode. Anything unrecognised — including `'strategy_year'`, which dates
 * nothing — describes the financial year, matching the engine's own default.
 */
export function describeProjectionPeriod(mode: string | null | undefined): ProjectionPeriodDescription {
    return mode === 'calendar_year' ? CALENDAR_YEAR : FINANCIAL_YEAR;
}

/**
 * The sentence a document uses to state which calendar it was cut on, and what that means for the
 * first row. Written for a reader who is not an accountant but may hand it to one.
 */
export function projectionPeriodConventionSentence(mode: string | null | undefined): string {
    const period = describeProjectionPeriod(mode);
    const base =
        `Projections run in ${period.nounPlural} (${period.span}) from today’s date, so the first year is a part-year ` +
        `covering only the days from today to the following ${period.closesOn}.`;
    if (!period.differsFromTaxYear) return base;
    // Only true of the calendar-year mode, and the reason it needs saying: the engine really does
    // assess tax over the period it reports, so a calendar row's tax is a modelling figure and not a
    // number that will ever appear on an ATO assessment.
    return (
        `${base} Australian tax and Centrelink years run 1 July to 30 June, so tax shown on a ` +
        `calendar-year row is assessed over that calendar year for modelling purposes and will not ` +
        `match a tax return.`
    );
}
